import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Package, AlertTriangle, Download, Save, Search, FileText, FileSpreadsheet, Pencil, RefreshCw, Link2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Stock = () => {
  const { token } = useAuth();
  const [produits, setProduits] = useState([]);
  const [reservations, setReservations] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProduit, setEditingProduit] = useState(null);
  const [editForm, setEditForm] = useState({
    modele: "",
    couleur: "",
    stock_initial: 0,
    stock_restant: 0,
    cost_unit: 0,
    prix_b2c_ttc: 0,
    prix_b2b_ht: 0,
    seuil_alerte: 10,
  });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [linking, setLinking] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchProduits = async () => {
    try {
      const [resP, resR] = await Promise.all([
        axios.get(`${API}/produits`, { headers }),
        axios.get(`${API}/produits/reservations`, { headers }),
      ]);
      setProduits(resP.data);
      setReservations(resR.data || {});
    } catch (error) {
      toast.error("Erreur lors du chargement du stock");
    } finally {
      setLoading(false);
    }
  };

  const getRes = (sku) => reservations[sku] || { livraisons: 0, devis: 0, total: 0 };

  const handleUpdateStock = async (produitId) => {
    try {
      await axios.put(`${API}/produits/${produitId}/stock`, 
        { stock_restant: parseInt(editValue) },
        { headers }
      );
      toast.success("Stock mis à jour !");
      setEditingId(null);
      fetchProduits();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const openEditDialog = (produit) => {
    setEditingProduit(produit);
    setEditForm({
      modele: produit.modele || "",
      couleur: produit.couleur || "",
      stock_initial: produit.stock_initial,
      stock_restant: produit.stock_restant,
      cost_unit: produit.cost_unit,
      prix_b2c_ttc: produit.prix_b2c_ttc,
      prix_b2b_ht: produit.prix_b2b_ht,
      seuil_alerte: produit.seuil_alerte ?? 10,
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/produits/${editingProduit.id}`, {
        modele: (editForm.modele || "").trim(),
        couleur: (editForm.couleur || "").trim(),
        stock_initial: parseInt(editForm.stock_initial),
        stock_restant: parseInt(editForm.stock_restant),
        cost_unit: parseFloat(editForm.cost_unit),
        prix_b2c_ttc: parseFloat(editForm.prix_b2c_ttc),
        prix_b2b_ht: parseFloat(editForm.prix_b2b_ht),
        seuil_alerte: parseInt(editForm.seuil_alerte),
      }, { headers });
      toast.success("Produit mis à jour !");
      setEditDialogOpen(false);
      setEditingProduit(null);
      fetchProduits();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const endpoint = format === 'pdf' ? `${API}/export/stock/pdf` : `${API}/export/stock`;
      const response = await axios.get(endpoint, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stock_topcocoon.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} téléchargé !`);
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleReset = async () => {
    if (resetConfirmText !== "REINITIALISER") {
      toast.error("Tape exactement REINITIALISER pour confirmer");
      return;
    }
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/reset-and-seed`, { confirm: "REINITIALISER" }, { headers });
      toast.success(`Réinitialisé — ${res.data.produits_crees} produits recréés`);
      setResetDialogOpen(false);
      setResetConfirmText("");
      fetchProduits();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  const handleRelierArticles = async () => {
    if (!window.confirm("Relier automatiquement les articles du stock aux livraisons qui n'en ont pas (à partir du texte de la commande) ?\n\nNe touche ni au stock ni au CA.")) return;
    setLinking(true);
    try {
      const res = await axios.post(`${API}/admin/relier-articles`, {}, { headers });
      const d = res.data;
      toast.success(`${d.reliees} reliée(s) + ${d.reparees || 0} réparée(s) ✅ (${d.deja_liees} déjà OK)`);
      if (d.nb_non_reconnues > 0) {
        toast.warning(`${d.nb_non_reconnues} non reconnue(s) — à relier à la main : ${d.non_reconnues.slice(0, 3).map(n => n.client).join(", ")}${d.nb_non_reconnues > 3 ? "…" : ""}`, { duration: 10000 });
      }
      fetchProduits();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur");
    } finally {
      setLinking(false);
    }
  };

  const handleRecalcStock = async () => {
    if (!window.confirm("⚠️ ATTENTION — Ce bouton va ÉCRASER le stock restant de TOUS les produits.\n\nIl recalcule : stock restant = stock initial − ventes − pertes.\nIl reconstruit aussi toutes les ventes à partir des livraisons livrées.\n\nÀ n'utiliser QUE si les stocks initiaux sont corrects ET les articles bien reliés.\n\nContinuer ?")) return;
    if (!window.confirm("Dernière confirmation : écraser les stocks actuels ?")) return;
    setRecalculating(true);
    try {
      // 1) Reconstruit les ventes depuis les livraisons livrées reliées à un produit
      await axios.post(`${API}/admin/recalculer-ventes`, {}, { headers });
      // 2) Recale le stock = stock_initial - ventes - pertes
      const res = await axios.post(`${API}/admin/recalculer-stock`, {}, { headers });
      toast.success(`Stock recalculé — ${res.data.produits_recalcules} produits mis à jour`);
      fetchProduits();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors du recalcul du stock");
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => {
    fetchProduits();
  }, []);

  const filteredProduits = produits.filter(p => 
    p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.modele.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.couleur.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stockBas = produits.filter(p => p.stock_restant < (p.seuil_alerte ?? 10));
  const totalValeur = produits.reduce((acc, p) => acc + p.valeur_stock, 0);
  const totalUnites = produits.reduce((acc, p) => acc + p.stock_restant, 0);
  const totalReserve = produits.reduce((acc, p) => acc + getRes(p.sku).total, 0);
  const surReserve = produits.filter(p => getRes(p.sku).total > p.stock_restant);

  // Group by model
  // Regroupement dynamique par modèle (gère Milan, Ghost, et tout futur modèle)
  const modelTextColors = ["text-indigo-600", "text-purple-600", "text-teal-600", "text-orange-600", "text-pink-600", "text-blue-600"];
  const groupedByModel = Object.entries(
    filteredProduits.reduce((acc, p) => {
      const m = p.modele || "Autre";
      (acc[m] = acc[m] || []).push(p);
      return acc;
    }, {})
  ).sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Stock</h1>
          <p className="text-slate-500 mt-1">Gestion des inventaires par modèle</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-stock-btn">
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-stock-csv-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Exporter en CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-stock-pdf-btn">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Exporter en PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            onClick={handleRelierArticles}
            disabled={linking}
            data-testid="relier-articles-btn"
          >
            <Link2 className={`w-4 h-4 mr-2 ${linking ? "animate-pulse" : ""}`} />
            {linking ? "Liaison..." : "Relier les articles"}
          </Button>
          <Button
            variant="outline"
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={handleRecalcStock}
            disabled={recalculating}
            data-testid="recalc-stock-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? "animate-spin" : ""}`} />
            {recalculating ? "Recalcul..." : "Recalculer le stock"}
          </Button>
          <Button
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => { setResetConfirmText(""); setResetDialogOpen(true); }}
            data-testid="open-reset-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
        </div>
      </div>

      {/* Reset + seed Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-md" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Réinitialiser toutes les données
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <p className="font-medium">⚠️ Action irréversible.</p>
              <p className="mt-1">Ça supprime : livraisons, tournées, ventes, clients, devis, charges, pertes, corbeille, notifications + l'ancien stock.</p>
              <p className="mt-1">Ça recrée automatiquement tes <strong>7 produits</strong> (Milan + Ghost).</p>
              <p className="mt-1">Conservé : ton compte, logo, prix, IBAN.</p>
            </div>
            <div>
              <Label className="text-sm">Tape <strong>REINITIALISER</strong> pour confirmer :</Label>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="REINITIALISER"
                className="mt-1"
                data-testid="reset-confirm-input"
              />
            </div>
            <Button
              className="w-full bg-red-600 hover:bg-red-700"
              onClick={handleReset}
              disabled={resetting || resetConfirmText !== "REINITIALISER"}
              data-testid="confirm-reset-btn"
            >
              {resetting ? "Réinitialisation..." : "Tout réinitialiser + recharger le stock"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md" data-testid="edit-product-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-indigo-600" />
              Modifier le produit
            </DialogTitle>
          </DialogHeader>
          {editingProduit && (
            <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{editingProduit.sku}</p>
                <p className="text-sm text-slate-500">{editingProduit.modele} - {editingProduit.couleur}</p>
                <p className="text-[10px] text-slate-400 mt-1">SKU inchangé — imports et livraisons restent liés</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du modèle</Label>
                  <Input
                    value={editForm.modele}
                    onChange={(e) => setEditForm({ ...editForm, modele: e.target.value })}
                    placeholder="MILAN, LOUIS…"
                    required
                    data-testid="edit-modele"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Couleur / Variante</Label>
                  <Input
                    value={editForm.couleur}
                    onChange={(e) => setEditForm({ ...editForm, couleur: e.target.value })}
                    placeholder="Noir (Black), Rose…"
                    required
                    data-testid="edit-couleur"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Stock Initial</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.stock_initial}
                    onChange={(e) => setEditForm({ ...editForm, stock_initial: e.target.value })}
                    required
                    data-testid="edit-stock-initial"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stock Restant</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editForm.stock_restant}
                    onChange={(e) => setEditForm({ ...editForm, stock_restant: e.target.value })}
                    required
                    data-testid="edit-stock-restant"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Coût Unitaire (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.cost_unit}
                  onChange={(e) => setEditForm({ ...editForm, cost_unit: e.target.value })}
                  required
                  data-testid="edit-cost-unit"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prix B2C TTC (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.prix_b2c_ttc}
                    onChange={(e) => setEditForm({ ...editForm, prix_b2c_ttc: e.target.value })}
                    required
                    data-testid="edit-prix-b2c"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prix B2B HT (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.prix_b2b_ht}
                    onChange={(e) => setEditForm({ ...editForm, prix_b2b_ht: e.target.value })}
                    required
                    data-testid="edit-prix-b2b"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Seuil d'alerte stock</Label>
                <Input
                  type="number"
                  min="0"
                  value={editForm.seuil_alerte}
                  onChange={(e) => setEditForm({ ...editForm, seuil_alerte: e.target.value })}
                  required
                  data-testid="edit-seuil-alerte"
                />
                <p className="text-xs text-slate-500">Alerte si stock descend en dessous de ce seuil</p>
              </div>

              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-edit-btn">
                <Save className="w-4 h-4 mr-2" />
                Enregistrer les modifications
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Alerts */}
      {surReserve.length > 0 && (
        <Card className="border-red-300 bg-red-50" data-testid="overreserve-alerts">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Sur-réservation ({surReserve.length} produits) — à réapprovisionner</p>
                <ul className="mt-2 space-y-1">
                  {surReserve.map(p => (
                    <li key={p.id} className="text-sm text-red-700">
                      {p.sku} - {p.couleur}: réservé <strong>{getRes(p.sku).total}</strong> pour <strong>{p.stock_restant}</strong> en stock (manque {getRes(p.sku).total - p.stock_restant})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {stockBas.length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="stock-alerts">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Stock bas ({stockBas.length} produits)</p>
                <ul className="mt-2 space-y-1">
                  {stockBas.map(p => (
                    <li key={p.id} className="text-sm text-amber-700">
                      {p.sku} - {p.couleur}: <strong>{p.stock_restant}</strong> restants (seuil: {p.seuil_alerte ?? 10})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total unités (physique)</p>
            <p className="text-2xl font-bold text-slate-800">{totalUnites.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card data-testid="summary-reserve">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Réservé (non livré)</p>
            <p className="text-2xl font-bold text-amber-600">{totalReserve.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Valeur totale stock</p>
            <p className="text-2xl font-bold text-indigo-600">{totalValeur.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Produits référencés</p>
            <p className="text-2xl font-bold text-slate-800">{produits.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Rechercher par SKU, modèle ou couleur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-stock"
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          {groupedByModel.length === 0 ? (
            <Card>
              <CardContent>
                <p className="text-center py-8 text-slate-500">Aucun produit trouvé</p>
              </CardContent>
            </Card>
          ) : (
            groupedByModel.map(([modele, items], gi) => (
              <Card key={modele} data-testid={`stock-card-${modele}`}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className={`w-5 h-5 ${modelTextColors[gi % modelTextColors.length]}`} />
                    {modele} ({items.length} références)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>SKU</TableHead>
                          <TableHead>Couleur</TableHead>
                          <TableHead className="text-right">Coût Unit.</TableHead>
                          <TableHead className="text-right">Prix B2C</TableHead>
                          <TableHead className="text-right">Prix B2B</TableHead>
                          <TableHead className="text-right">Stock Initial</TableHead>
                          <TableHead className="text-right">Stock Restant</TableHead>
                          <TableHead className="text-right">Réservé</TableHead>
                          <TableHead className="text-right">Disponible</TableHead>
                          <TableHead className="text-right">Valeur Stock</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((produit) => (
                          <TableRow key={produit.id} data-testid={`stock-row-${produit.id}`}>
                            <TableCell className="font-mono text-sm">{produit.sku}</TableCell>
                            <TableCell>{produit.couleur}</TableCell>
                            <TableCell className="text-right">{produit.cost_unit.toFixed(2)} €</TableCell>
                            <TableCell className="text-right">{produit.prix_b2c_ttc.toFixed(2)} €</TableCell>
                            <TableCell className="text-right">{produit.prix_b2b_ht.toFixed(2)} €</TableCell>
                            <TableCell className="text-right">{produit.stock_initial}</TableCell>
                            <TableCell className="text-right">
                              {editingId === produit.id ? (
                                <Input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-20 text-right"
                                  autoFocus
                                  data-testid={`edit-stock-input-${produit.id}`}
                                />
                              ) : (
                                <span className={produit.stock_restant < (produit.seuil_alerte ?? 10) ? "text-red-600 font-semibold" : ""}>
                                  {produit.stock_restant}
                                  {produit.stock_restant < (produit.seuil_alerte ?? 10) && (
                                    <Badge variant="destructive" className="ml-2 text-xs">Bas</Badge>
                                  )}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-amber-600 font-medium" title={`Livraisons: ${getRes(produit.sku).livraisons} · Devis: ${getRes(produit.sku).devis}`} data-testid={`reserve-${produit.id}`}>
                                {getRes(produit.sku).total}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {(() => {
                                const dispo = produit.stock_restant - getRes(produit.sku).total;
                                return <span className={dispo < 0 ? "text-red-600 font-bold" : "font-medium text-emerald-700"} data-testid={`dispo-${produit.id}`}>{dispo}</span>;
                              })()}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {produit.valeur_stock.toFixed(2)} €
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {editingId === produit.id ? (
                                  <Button
                                    size="sm"
                                    onClick={() => handleUpdateStock(produit.id)}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    data-testid={`save-stock-${produit.id}`}
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditDialog(produit)}
                                    className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                    data-testid={`edit-product-${produit.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default Stock;
